import React, { useContext } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ThemeContext } from 'styled-components';
import * as yup from 'yup';

import { Gray, Purple } from 'constants/colors';
import {
  ActionPortal,
  ButtonWithIcon,
  DefaultActionButtons,
  Form,
  Icon,
  MaskedInput,
  Step,
  Text,
} from 'components';
import banner from 'assets/images/banner_purple_bg.jpeg';

const MINIMUM_TRACKERS = 10;
const MINIMUM_CAMERAS = 5;

const STEPS = {
  QUANTITY_TYPES: 2,
  CAMERA_TYPES: 3,
};

const QuantityOfTrackers = ({
  initialValues: defaultValues,
  onSubmit,
  onCancel,
  theme,
}) => {
  const conteleTrackerCount = defaultValues?.vehicleManager?.conteleTrackerCount || 0;
  const conteleCamerasCount = defaultValues?.vehicleManager?.conteleCamerasCount || 0;

  const isTrackers = conteleTrackerCount > 0;
  const totalEquipmentCount = isTrackers ? conteleTrackerCount : conteleCamerasCount;

  const equipmentType = isTrackers ? 'rastreadores' : 'câmeras';
  const minimumQuantity = isTrackers ? MINIMUM_TRACKERS : MINIMUM_CAMERAS;
  const defaultQuantity = minimumQuantity;

  const schema = yup.object().shape({
    isAllTrackers: yup.boolean().transform((value) => !!value),
    trackersQuantity: yup.number().when('isAllTrackers', {
      is: false,
      then: yup
        .number()
        .min(
          minimumQuantity,
          `A quantidade mínima é de ${minimumQuantity} ${equipmentType}`,
        )
        .max(
          totalEquipmentCount,
          `A quantidade máxima é de ${totalEquipmentCount} ${equipmentType}`,
        )
        .typeError('Digite um número válido'),
      otherwise: yup.number().nullable(),
    }),
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: 'all',
    defaultValues: {
      isAllTrackers: defaultValues?.isAllTrackers || true,
      trackersQuantity: defaultValues?.trackersQuantity || defaultQuantity,
    },
    resolver: yupResolver(schema),
  });

  const trackersQuantity = watch('trackersQuantity');
  const isAllTrackers = watch('isAllTrackers');

  const isFormValid = isValid && (
    isAllTrackers
    || (trackersQuantity >= minimumQuantity && trackersQuantity <= totalEquipmentCount)
  );

  const calculateNextStepPosition = () => {
    const conteleTrackerCount = defaultValues?.vehicleManager?.conteleTrackerCount || 0;
    const conteleCamerasCount = defaultValues?.vehicleManager?.conteleCamerasCount || 0;

    if (conteleTrackerCount > 0) {
      return STEPS.QUANTITY_TYPES;
    }

    if (conteleCamerasCount > 0) {
      return STEPS.CAMERA_TYPES;
    }

    return STEPS.QUANTITY_TYPES;
  };

  const handleFormSubmit = (values) => {
    const formValues = {
      ...values,
      nextStepIndexPosition: calculateNextStepPosition(),
    };
    onSubmit(formValues);
  };

  const submitForm = (e) => {
    e.preventDefault();
    handleSubmit(handleFormSubmit)();
  };

  return (
    <Step
      headerTitle="Envio dos equipamentos"
      style={{ padding: '0px 22px' }}
    >
      <Form onKeyPress={(e) => e.key === 'Enter' && submitForm(e)}>
        <div
          style={{
            display: 'flex',
            position: 'relative',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <img
            src={banner}
            alt="Banner roxo"
            style={{
              width: '100%',
              height: '119px',
              objectFit: 'cover',
              borderRadius: 10,
              position: 'absolute',
              top: 0,
              zIndex: 10,
            }}
          />
          <section
            style={{
              position: 'absolute',
              top: 40,
              zIndex: 14,
              width: '100%',
              maxWidth: 800,
              borderWidth: 1,
              padding: '18px 24px',
              borderRadius: 10,
              borderStyle: 'solid',
              borderColor: theme.newPalette.background.gray[300],
              backgroundColor: '#fff',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: 16, fontWeight: 500 }}>
                Devemos enviar todos os
                {' '}
                <span
                  style={{
                    fontWeight: 'bold',
                    color: theme.newPalette.background.purple,
                  }}
                >
                  {totalEquipmentCount}
                  {' '}
                  {equipmentType}
                </span>
                {' '}
                no primeiro envio?
                {' '}
                <span
                  style={{
                    fontWeight: 'bold',
                    color: theme.newPalette.background.red,
                  }}
                >
                  *
                </span>
              </p>
              <article
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginTop: 16,
                }}
              >
                <Controller
                  control={control}
                  name="isAllTrackers"
                  render={({ onChange }) => (
                    <Option
                      isActive={isAllTrackers === true}
                      onClick={() => onChange(true)}
                    >
                      Sim, pode me enviar a quantidade total
                    </Option>
                  )}
                />
                <Controller
                  control={control}
                  name="isAllTrackers"
                  render={({ onChange }) => (
                    <Option
                      isActive={isAllTrackers === false}
                      onClick={() => onChange(false)}
                    >
                      Não, quero receber uma quantidade menor
                    </Option>
                  )}
                />
              </article>
            </div>
            {!isAllTrackers && (
              <div
                style={{
                  display: 'flex',
                  marginTop: 16,
                  flexDirection: 'column',
                }}
              >
                <p style={{ fontSize: 16, fontWeight: 500 }}>
                  Quantos podemos enviar no primeiro lote?
                  {' '}
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: theme.newPalette.background.red,
                    }}
                  >
                    *
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                  <ButtonWithIcon
                    icon="minus"
                    customBackgroundColor={Gray[100]}
                    border={`1px solid ${Gray[250]}`}
                    customIconColor={Gray[400]}
                    height="32px"
                    iconOptions={{ width: '14px', height: '14px' }}
                    onClick={() => {
                      if (trackersQuantity <= minimumQuantity) return;
                      setValue('trackersQuantity', (trackersQuantity || 0) - 1);
                    }}
                  />
                  <MaskedInput
                    control={control}
                    errors={errors}
                    name="trackersQuantity"
                    placeholder={`Digite a quantidade de ${equipmentType}`}
                    format="numeric"
                    containerWidth="100%"
                    min={minimumQuantity}
                    style={{
                      border: `1px solid ${Gray[250]}`,
                      borderRadius: '5px',
                    }}
                  />
                  <ButtonWithIcon
                    icon="plus"
                    customBackgroundColor={Gray[100]}
                    border={`1px solid ${Gray[250]}`}
                    customIconColor={Gray[400]}
                    height="32px"
                    iconOptions={{ width: '14px', height: '14px' }}
                    onClick={() => setValue('trackersQuantity', (trackersQuantity || 0) + 1)}
                  />
                </div>
                {
                  totalEquipmentCount - (trackersQuantity || 0) > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Icon
                        icon="exclamationCircle"
                        color={Gray[300]}
                        style={{ width: 16, height: 16 }}
                      />
                      <Text
                        color={Gray[300]}
                        fontSize="14px"
                        lineHeight="22px"
                      >
                        Os outros
                        {' '}
                        <span style={{ fontWeight: 'bold', color: Gray[400] }}>
                          {totalEquipmentCount - (trackersQuantity || 0)}
                          {' '}
                          equipamentos
                        </span>
                        {' '}
                        ficarão disponíveis e podem ser solicitados pelo suporte a qualquer momento.
                      </Text>
                    </div>
                  )
                }
              </div>
            )}
            <ActionPortal>
              <DefaultActionButtons
                theme={theme}
                isValid={isFormValid}
                backButtonStyle={{
                  iconPosition: 'left',
                  border: 'none',
                  customBackgroundColor: 'transparent',
                  customIconColor: Gray[400],
                  customTextColor: Gray[400],
                  title: 'Passo Anterior',
                }}
                nextButtonStyle={{
                  customBackgroundColor: Purple[500],
                  customIconColor: Gray[100],
                  customTextColor: Gray[100],
                  title: 'Próximo Passo',
                }}
                onCancel={onCancel}
                onSubmit={() => handleSubmit(handleFormSubmit)()}
              />
            </ActionPortal>
          </section>
        </div>
      </Form>
    </Step>
  );
};

const Option = ({ isActive, onClick, children }) => {
  const theme = useContext(ThemeContext);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        fontSize: 16,
        fontWeight: 500,
        padding: 10,
        backgroundColor:
          theme.newPalette.background[isActive ? 'purple' : 'white'],
        color: theme.newPalette.background[isActive ? 'white' : '#21252E'],
        outline: 'none',
        borderRadius: 8,
        border: 2,
        borderStyle: 'solid',
        borderColor: isActive
          ? theme.newPalette.background.purple
          : theme.newPalette.background.gray[300],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        cursor: 'pointer',
      }}
    >
      <span>{children}</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z"
          fill={isActive ? '#B55FFE' : '#E5E5E5'}
        />
        {isActive && (
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.0051 4.73418C11.1551 4.58471 11.3576 4.50059 11.5688 4.5C11.7801 4.49942 11.983 4.58241 12.1339 4.73105C12.2847 4.87968 12.3713 5.08201 12.3749 5.29429C12.3785 5.50657 12.2988 5.71174 12.1531 5.86546L7.87389 11.2417C7.80034 11.3214 7.71158 11.3853 7.6129 11.4296C7.51423 11.474 7.40767 11.4978 7.29959 11.4999C7.19152 11.5019 7.08415 11.482 6.98391 11.4413C6.88367 11.4007 6.79261 11.3401 6.71619 11.2633L3.88088 8.41246C3.80189 8.33848 3.73854 8.24927 3.6946 8.15015C3.65066 8.05103 3.62703 7.94403 3.62513 7.83553C3.62322 7.72703 3.64308 7.61926 3.68351 7.51864C3.72395 7.41802 3.78413 7.32662 3.86048 7.24989C3.93682 7.17316 4.02776 7.11267 4.12786 7.07203C4.22797 7.03138 4.3352 7.01143 4.44314 7.01334C4.55109 7.01525 4.65755 7.039 4.75617 7.08317C4.85479 7.12733 4.94355 7.19101 5.01715 7.2704L7.26181 9.52542L10.9847 4.75788C10.9913 4.74953 10.9985 4.74162 11.0061 4.73418H11.0051Z"
            fill="#F7F7F7"
          />
        )}
      </svg>
    </button>
  );
};

export default QuantityOfTrackers;
